import { useEffect, useRef, useState } from "react";
import {
  POST_TYPES,
  getCommunityPosts,
  createPostWithMentions,
  updatePost,
  deletePost,
  markQuestionAnswered,
  uploadPostPhoto,
  deletePostPhoto,
  getCommentsForPost,
  addCommentWithMentions,
  deleteComment,
  getReactionsForPost,
  togglePostReaction,
  getReactionsForComment,
  toggleCommentReaction,
  getCommunityMembers,
} from "./communityFeed";
import { writeUserNotification } from "./userNotifications";
import { writeActivityEvent } from "./adminActivity";

const EMOJI_OPTIONS = ["❤️", "🔥", "🙌", "😂", "😮", "🎉"];

const card = {
  background: "#f5faff",
  borderRadius: "20px",
  padding: "18px",
  border: "2px solid #dbeafe",
  boxShadow: "0 4px 14px rgba(121,94,148,0.07)",
  textAlign: "left",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 14px",
  borderRadius: "12px",
  border: "2px solid #bfdbfe",
  background: "#f8fbff",
  color: "#1e3a5f",
  fontSize: "14px",
};

const btnPrimary = {
  padding: "10px 18px",
  borderRadius: "12px",
  border: "2px solid #2563eb",
  background: "#e0f2fe",
  color: "#1e3a5f",
  fontWeight: "bold",
  cursor: "pointer",
  fontSize: "14px",
};

const btnGhost = {
  padding: "6px 12px",
  borderRadius: "10px",
  border: "2px solid #bfdbfe",
  background: "none",
  color: "#4b6b8a",
  cursor: "pointer",
  fontSize: "13px",
};

function timeAgo(ts) {
  if (!ts) return "";
  const date = ts.toDate?.() || new Date(ts);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function TypeTag({ typeKey }) {
  const type = POST_TYPES.find((t) => t.key === typeKey) || POST_TYPES[3];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      padding: "3px 10px", borderRadius: "999px", fontSize: "12px",
      fontWeight: "bold", color: "#1e3a5f",
      background: type.color, border: `1.5px solid ${type.border}`,
    }}>
      {type.emoji} {type.label}
    </span>
  );
}

function ReactionBar({ postId, postAuthorId, currentUser, reactions, onToggle }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hoveredEmoji, setHoveredEmoji] = useState(null);

  const grouped = EMOJI_OPTIONS.map((emoji) => {
    const matches = reactions.filter((r) => r.emoji === emoji);
    const users = matches.map((r) => r.userName || "").filter(Boolean);
    return { emoji, count: matches.length, reactedByMe: matches.some((r) => r.userId === currentUser.uid), users };
  }).filter((r) => r.count > 0);

  async function handle(emoji) {
    setPickerOpen(false);
    const alreadyReacted = reactions.some((r) => r.emoji === emoji && r.userId === currentUser.uid);
    // Optimistic
    onToggle(emoji);
    try {
      await togglePostReaction({ postId, emoji, currentUser });
      // Notify post author when adding a reaction (not removing)
      if (!alreadyReacted && postAuthorId && postAuthorId !== currentUser.uid) {
        writeUserNotification({
          toUserId: postAuthorId,
          type: "reaction",
          message: `${currentUser.name || "Someone"} reacted ${emoji} to your post`,
          fromName: currentUser.name || "",
          fromAvatar: currentUser.avatar || "",
          postId,
        });
      }
    } catch (err) {
      console.error("Reaction error:", err);
      onToggle(emoji); // revert
    }
  }

  return (
    <div style={{ marginTop: "10px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
        {grouped.map((r) => (
          <div key={r.emoji} style={{ position: "relative" }}
            onMouseEnter={() => setHoveredEmoji(r.emoji)}
            onMouseLeave={() => setHoveredEmoji(null)}
          >
            <button onClick={() => handle(r.emoji)} style={{
              padding: "4px 10px", borderRadius: "999px", fontSize: "13px",
              border: r.reactedByMe ? "2px solid #2563eb" : "2px solid #bfdbfe",
              background: r.reactedByMe ? "#e0f2fe" : "#f5faff",
              color: "#1e3a5f", cursor: "pointer",
            }}>
              {r.emoji} {r.count}
            </button>
            {hoveredEmoji === r.emoji && r.users.length > 0 && (
              <div style={{
                position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
                background: "#1e3a5f", color: "#fff", padding: "5px 10px", borderRadius: "8px",
                fontSize: "11px", whiteSpace: "nowrap", zIndex: 20, pointerEvents: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
              }}>
                {r.users.join(", ")}
              </div>
            )}
          </div>
        ))}
        <button onClick={() => setPickerOpen((p) => !p)} style={{
          padding: "4px 10px", borderRadius: "999px", border: "2px solid #bfdbfe",
          background: "#f8fbff", color: "#6b8cb0", cursor: "pointer", fontSize: "13px",
        }}>+</button>
      </div>
      {pickerOpen && (
        <div style={{ display: "flex", gap: "6px", marginTop: "8px", flexWrap: "wrap" }}>
          {EMOJI_OPTIONS.map((emoji) => (
            <button key={emoji} onClick={() => handle(emoji)} style={{
              padding: "6px 10px", borderRadius: "999px", border: "2px solid #bfdbfe",
              background: "#f5faff", cursor: "pointer", fontSize: "16px",
            }}>{emoji}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// CommentReactionBar receives reactions and a toggle handler from PostCard
// so it never fetches on its own (which would race with Firestore propagation)
function CommentReactionBar({ commentId, currentUser, reactions, onToggle }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hoveredEmoji, setHoveredEmoji] = useState(null);

  const grouped = EMOJI_OPTIONS.map((emoji) => {
    const matches = reactions.filter((r) => r.emoji === emoji);
    const users = matches.map((r) => r.userName || "").filter(Boolean);
    return { emoji, count: matches.length, reactedByMe: matches.some((r) => r.userId === currentUser.uid), users };
  }).filter((r) => r.count > 0);

  async function handle(emoji) {
    setPickerOpen(false);
    onToggle(commentId, emoji);
  }

  return (
    <div style={{ marginTop: "6px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
        {grouped.map((r) => (
          <div key={r.emoji} style={{ position: "relative" }}
            onMouseEnter={() => setHoveredEmoji(r.emoji)}
            onMouseLeave={() => setHoveredEmoji(null)}
          >
            <button onClick={() => handle(r.emoji)} style={{
              padding: "2px 8px", borderRadius: "999px", fontSize: "12px",
              border: r.reactedByMe ? "1.5px solid #2563eb" : "1.5px solid #bfdbfe",
              background: r.reactedByMe ? "#e0f2fe" : "#f5faff",
              color: "#1e3a5f", cursor: "pointer",
            }}>
              {r.emoji} {r.count}
            </button>
            {hoveredEmoji === r.emoji && r.users.length > 0 && (
              <div style={{
                position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
                background: "#1e3a5f", color: "#fff", padding: "5px 10px", borderRadius: "8px",
                fontSize: "11px", whiteSpace: "nowrap", zIndex: 20, pointerEvents: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
              }}>
                {r.users.join(", ")}
              </div>
            )}
          </div>
        ))}
        <button onClick={() => setPickerOpen((p) => !p)} style={{
          padding: "2px 8px", borderRadius: "999px", border: "1.5px solid #bfdbfe",
          background: "#f8fbff", color: "#6b8cb0", cursor: "pointer", fontSize: "12px",
        }}>+</button>
      </div>
      {pickerOpen && (
        <div style={{ display: "flex", gap: "4px", marginTop: "6px", flexWrap: "wrap" }}>
          {EMOJI_OPTIONS.map((emoji) => (
            <button key={emoji} onClick={() => handle(emoji)} style={{
              padding: "4px 8px", borderRadius: "999px", border: "1.5px solid #bfdbfe",
              background: "#f5faff", cursor: "pointer", fontSize: "14px",
            }}>{emoji}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function PostCard({ post, currentUser, isAdmin, members, onDeleted, onAnswered, onEdited, highlighted }) {
  const [reactions, setReactions] = useState([]);
  const [comments, setComments] = useState([]);
  // commentReactions: { [commentId]: reaction[] }
  const [commentReactions, setCommentReactions] = useState({});
  const [showComments, setShowComments] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentMentionQuery, setCommentMentionQuery] = useState("");
  const [commentMentionOpen, setCommentMentionOpen] = useState(false);
  const [commentMentionIds, setCommentMentionIds] = useState([]);
  const [commentMentionAll, setCommentMentionAll] = useState(false);
  const [confirmDeletePost, setConfirmDeletePost] = useState(false);
  const [confirmDeleteCommentId, setConfirmDeleteCommentId] = useState(null);
  const [showPhotoIndex, setShowPhotoIndex] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [editType, setEditType] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const commentMentionCandidates = (members || []).filter((m) => {
    if (m.id === currentUser.uid) return false;
    const q = commentMentionQuery.trim().toLowerCase();
    if (!q) return true;
    return (m.name || "").toLowerCase().includes(q);
  }).slice(0, 6);

  function applyAtToken(baseText, replacement) {
    return baseText.replace(/@([\w.]*)$/, replacement);
  }

  function handleCommentDraftChange(next) {
    setCommentDraft(next);
    const match = next.match(/(?:^|\s)@([\w.]*)$/);
    setCommentMentionQuery(match ? match[1] : "");
    setCommentMentionOpen(/(?:^|\s)@[\w.]*$/.test(next));
  }

  function handleCommentMentionPick(member) {
    setCommentDraft((prev) => applyAtToken(prev, `@${member.name} `));
    setCommentMentionIds((prev) => Array.from(new Set([...prev, member.id])));
    setCommentMentionOpen(false);
  }

  function handleCommentMentionAll() {
    setCommentDraft((prev) => applyAtToken(prev, "@everyone "));
    setCommentMentionAll(true);
    setCommentMentionOpen(false);
  }

  function handleCommentMentionTrigger() {
    setCommentDraft((prev) => `${prev}${prev.endsWith(" ") || prev.length === 0 ? "" : " "}@`);
    setCommentMentionQuery("");
    setCommentMentionOpen(true);
  }

  useEffect(() => {
    getReactionsForPost(post.id).then(setReactions);
    getCommentsForPost(post.id).then((fetchedComments) => {
      setComments(fetchedComments);
      // Load all comment reactions at once so CommentReactionBar never fetches independently
      Promise.all(
        fetchedComments.map((c) =>
          getReactionsForComment(c.id).then((r) => ({ id: c.id, reactions: r }))
        )
      ).then((results) => {
        const map = {};
        results.forEach(({ id, reactions: r }) => { map[id] = r; });
        setCommentReactions(map);
      });
    });
  }, [post.id]);

  // Optimistic toggle for comment reactions — updates local state immediately,
  // then writes to Firestore. Never re-fetches (avoids race with propagation).
  async function handleCommentReactionToggle(commentId, emoji) {
    const prev = commentReactions[commentId] || [];
    const alreadyReacted = prev.some((r) => r.emoji === emoji && r.userId === currentUser.uid);
    // Apply optimistic update
    setCommentReactions((prevMap) => {
      const existing = prevMap[commentId] || [];
      const updated = alreadyReacted
        ? existing.filter((r) => !(r.emoji === emoji && r.userId === currentUser.uid))
        : [...existing, { emoji, userId: currentUser.uid, userName: currentUser.name || "", commentId }];
      return { ...prevMap, [commentId]: updated };
    });
    try {
      await toggleCommentReaction({ commentId, emoji, currentUser });
      // Notify comment author when adding a reaction (not removing)
      if (!alreadyReacted) {
        const comment = comments.find((c) => c.id === commentId);
        if (comment?.authorId && comment.authorId !== currentUser.uid) {
          writeUserNotification({
            toUserId: comment.authorId,
            type: "reaction",
            message: `${currentUser.name || "Someone"} reacted ${emoji} to your comment`,
            fromName: currentUser.name || "",
            fromAvatar: currentUser.avatar || "",
            postId: post.id,
          });
        }
      }
    } catch (err) {
      console.error("Comment reaction error:", err);
      // Revert on error
      setCommentReactions((prevMap) => {
        const existing = prevMap[commentId] || [];
        const reverted = alreadyReacted
          ? [...existing, { emoji, userId: currentUser.uid, commentId }]
          : existing.filter((r) => !(r.emoji === emoji && r.userId === currentUser.uid));
        return { ...prevMap, [commentId]: reverted };
      });
    }
  }

  function optimisticToggle(emoji) {
    setReactions((prev) => {
      const alreadyReacted = prev.some((r) => r.emoji === emoji && r.userId === currentUser.uid);
      if (alreadyReacted) {
        return prev.filter((r) => !(r.emoji === emoji && r.userId === currentUser.uid));
      }
      return [...prev, { emoji, userId: currentUser.uid, userName: currentUser.name || "", postId: post.id }];
    });
  }

  async function handleAddComment() {
    if (!commentDraft.trim()) return;
    setCommentSaving(true);
    try {
      await addCommentWithMentions({
        postId: post.id,
        text: commentDraft,
        currentUser,
        mentionUserIds: commentMentionIds,
        mentionAll: commentMentionAll,
      });
      const updated = await getCommentsForPost(post.id);
      setComments(updated);

      // ── Notifications ──────────────────────────────────────────────────
      // 1. Notify post author (if not the commenter)
      if (post.authorId && post.authorId !== currentUser.uid) {
        writeUserNotification({
          toUserId: post.authorId,
          type: "comment",
          message: `${currentUser.name || "Someone"} commented on your post`,
          fromName: currentUser.name || "",
          fromAvatar: currentUser.avatar || "",
          postId: post.id,
        });
      }

      // 2. Notify other commenters on this post (thread notification)
      const threadUserIds = new Set(
        updated
          .filter((c) => c.authorId && c.authorId !== currentUser.uid && c.authorId !== post.authorId)
          .map((c) => c.authorId)
      );
      threadUserIds.forEach((uid) => {
        writeUserNotification({
          toUserId: uid,
          type: "thread",
          message: `${currentUser.name || "Someone"} also commented on a post you're in`,
          fromName: currentUser.name || "",
          fromAvatar: currentUser.avatar || "",
          postId: post.id,
        });
      });

      // 3. Notify mentioned users in comment
      const commentMentionTargets = commentMentionAll
        ? (members || []).filter((m) => m.id !== currentUser.uid)
        : (members || []).filter((m) => commentMentionIds.includes(m.id) && m.id !== currentUser.uid);
      commentMentionTargets.forEach((m) => {
        writeUserNotification({
          toUserId: m.id,
          type: "mention",
          message: `${currentUser.name || "Someone"} mentioned you in a comment`,
          fromName: currentUser.name || "",
          fromAvatar: currentUser.avatar || "",
          postId: post.id,
        });
      });

      setCommentDraft("");
      setCommentMentionIds([]);
      setCommentMentionAll(false);
      setCommentMentionQuery("");
      setCommentMentionOpen(false);
    } finally {
      setCommentSaving(false);
    }
  }

  async function handleDeleteComment(commentId) {
    await deleteComment(commentId);
    setComments((prev) => prev.filter((c) => c.id !== commentId));
  }

  const canDelete = post.authorId === currentUser.uid || isAdmin;
  const canEdit = post.authorId === currentUser.uid;
  const type = POST_TYPES.find((t) => t.key === (editing ? editType : post.type)) || POST_TYPES[3];

  async function handleSaveEdit() {
    if (!editText.trim()) return;
    setEditSaving(true);
    try {
      await updatePost(post.id, { text: editText, type: editType });
      onEdited?.(post.id, { text: editText, type: editType });
      setEditing(false);
    } catch (err) {
      console.error("Edit failed:", err);
    } finally {
      setEditSaving(false);
    }
  }

  return (
    <div
      id={`post-${post.id}`}
      style={{
        ...card,
        borderColor: highlighted ? "#2563eb" : type.border,
        boxShadow: highlighted
          ? "0 0 0 3px #93c5fd, 0 4px 14px rgba(30,58,138,0.13)"
          : card.boxShadow,
        transition: "border-color 0.4s, box-shadow 0.4s",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "12px" }}>
        {post.authorAvatar ? (
          <img src={post.authorAvatar} alt={post.authorName}
            style={{ width: "38px", height: "38px", borderRadius: "999px", objectFit: "cover", border: "2px solid #bfdbfe", flexShrink: 0 }} />
        ) : (
          <div style={{ width: "38px", height: "38px", borderRadius: "999px", background: "#bfdbfe", flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>{post.authorName || "User"}</span>
            <TypeTag typeKey={post.type} />
            {post.type === "question" && (
              <span style={{
                fontSize: "11px", fontWeight: "bold", padding: "2px 8px", borderRadius: "999px",
                background: post.isAnswered ? "#f0fdf4" : "#fff7ed",
                border: post.isAnswered ? "1.5px solid #bbf7d0" : "1.5px solid #fed7aa",
                color: post.isAnswered ? "#166534" : "#9a3412",
              }}>
                {post.isAnswered ? "✓ Answered" : "Needs answer"}
              </span>
            )}
          </div>
          <div style={{ color: "#6b8cb0", fontSize: "12px", marginTop: "2px" }}>
            {timeAgo(post.createdAt)}
            {post.editedAt && <span style={{ marginLeft: "4px", color: "#9ca3af" }}>· edited</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          {post.type === "question" && isAdmin && (
            <button onClick={() => onAnswered(post.id, !post.isAnswered)} style={{ ...btnGhost, fontSize: "12px", padding: "4px 8px" }}>
              {post.isAnswered ? "Unmark" : "Mark answered"}
            </button>
          )}
          {canEdit && !editing && !confirmDeletePost && (
            <button
              onClick={() => { setEditText(post.text || ""); setEditType(post.type || "general"); setEditing(true); }}
              style={{ ...btnGhost, padding: "4px 8px", fontSize: "12px" }}
              title="Edit post"
            >✏️</button>
          )}
          {canDelete && (
            confirmDeletePost ? (
              <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                <button onClick={() => onDeleted(post.id)} style={{ ...btnGhost, padding: "3px 8px", fontSize: "11px", color: "#ef4444", borderColor: "#ef4444" }}>Delete</button>
                <button onClick={() => setConfirmDeletePost(false)} style={{ ...btnGhost, padding: "3px 8px", fontSize: "11px" }}>Cancel</button>
              </div>
            ) : (
              <button onClick={() => setConfirmDeletePost(true)} style={{ ...btnGhost, padding: "4px 8px", fontSize: "12px", color: "#93c5fd" }}>✕</button>
            )
          )}
        </div>
      </div>

      {/* Text / inline edit form */}
      {editing ? (
        <div style={{ marginBottom: "12px" }}>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
            {POST_TYPES.map((t) => (
              <button key={t.key} onClick={() => setEditType(t.key)} style={{
                padding: "5px 12px", borderRadius: "999px", fontSize: "12px", fontWeight: "bold", cursor: "pointer",
                border: editType === t.key ? `2px solid ${t.border}` : "2px solid #bfdbfe",
                background: editType === t.key ? t.color : "#f5faff", color: "#1e3a5f",
              }}>
                {t.emoji} {t.label}
              </button>
            ))}
          </div>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: "vertical", marginBottom: "10px" }}
            autoFocus
          />
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={handleSaveEdit} disabled={editSaving || !editText.trim()} style={btnPrimary}>
              {editSaving ? "Saving..." : "Save"}
            </button>
            <button onClick={() => setEditing(false)} style={btnGhost}>Cancel</button>
          </div>
        </div>
      ) : post.text ? (
        <p style={{ margin: "0 0 12px", color: "#243b53", fontSize: "15px", lineHeight: "1.6", whiteSpace: "pre-wrap", textAlign: "left" }}>
          {post.text}
        </p>
      ) : null}

      {/* Photos */}
      {post.photos?.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: post.photos.length === 1 ? "1fr" : "repeat(auto-fill, minmax(120px, 1fr))",
          gap: "8px", marginBottom: "12px",
        }}>
          {post.photos.map((p, i) => (
            <img key={p.id || i} src={p.url} alt="" onClick={() => setShowPhotoIndex(i)}
              style={{ width: "100%", aspectRatio: post.photos.length === 1 ? "16/9" : "1", objectFit: "cover", borderRadius: "12px", border: "2px solid #bfdbfe", cursor: "pointer" }} />
          ))}
        </div>
      )}

      {/* Fullscreen photo viewer */}
      {showPhotoIndex !== null && (
        <div onClick={() => setShowPhotoIndex(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
        }}>
          <img src={post.photos[showPhotoIndex].url} alt=""
            style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: "16px", objectFit: "contain" }} />
        </div>
      )}

      {/* Reactions */}
      <ReactionBar postId={post.id} postAuthorId={post.authorId} currentUser={currentUser} reactions={reactions} onToggle={optimisticToggle} />

      {/* Comments toggle */}
      <button onClick={() => setShowComments((p) => !p)} style={{
        marginTop: "10px", background: "none", border: "none", padding: 0,
        color: "#4b6b8a", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
      }}>
        {comments.length > 0 ? `${comments.length} comment${comments.length !== 1 ? "s" : ""}` : "Comment"}
        {showComments ? " ▲" : " ▼"}
      </button>

      {/* Comments section */}
      {showComments && (
        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #bfdbfe" }}>
          {comments.map((c) => (
            <div key={c.id} style={{ display: "flex", gap: "8px", marginBottom: "12px", alignItems: "flex-start" }}>
              {c.authorAvatar ? (
                <img src={c.authorAvatar} alt={c.authorName}
                  style={{ width: "28px", height: "28px", borderRadius: "999px", objectFit: "cover", border: "2px solid #bfdbfe", flexShrink: 0 }} />
              ) : (
                <div style={{ width: "28px", height: "28px", borderRadius: "999px", background: "#bfdbfe", flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, background: "#f8fbff", borderRadius: "12px", padding: "8px 12px", border: "1px solid #bfdbfe" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px" }}>{c.authorName || "User"}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ color: "#6b8cb0", fontSize: "11px" }}>{timeAgo(c.createdAt)}</span>
                    {(c.authorId === currentUser.uid || isAdmin) && (
                      confirmDeleteCommentId === c.id ? (
                        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                          <button onClick={() => { handleDeleteComment(c.id); setConfirmDeleteCommentId(null); }} style={{ background: "none", border: "1px solid #ef4444", borderRadius: "6px", padding: "1px 6px", color: "#ef4444", cursor: "pointer", fontSize: "10px" }}>Delete</button>
                          <button onClick={() => setConfirmDeleteCommentId(null)} style={{ background: "none", border: "1px solid #bfdbfe", borderRadius: "6px", padding: "1px 6px", color: "#6b8cb0", cursor: "pointer", fontSize: "10px" }}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmDeleteCommentId(c.id)}
                          style={{ background: "none", border: "none", padding: 0, color: "#93c5fd", cursor: "pointer", fontSize: "11px" }}>✕</button>
                      )
                    )}
                  </div>
                </div>
                <p style={{ margin: "4px 0 0", color: "#243b53", fontSize: "13px", lineHeight: "1.5", whiteSpace: "pre-wrap", textAlign: "left" }}>{c.text}</p>
                <CommentReactionBar
                  commentId={c.id}
                  currentUser={currentUser}
                  reactions={commentReactions[c.id] || []}
                  onToggle={handleCommentReactionToggle}
                />
              </div>
            </div>
          ))}

          {/* Comment input */}
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
            <textarea value={commentDraft} onChange={(e) => handleCommentDraftChange(e.target.value)}
              placeholder="Write a comment..."
              rows={2}
              style={{ ...inputStyle, flex: 1, resize: "none" }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
            />
            <button onClick={handleCommentMentionTrigger} style={{ ...btnGhost, padding: "10px 12px", fontWeight: "bold" }}>@</button>
            <button onClick={handleAddComment} disabled={commentSaving || !commentDraft.trim()} style={{ ...btnPrimary, padding: "10px 14px" }}>
              {commentSaving ? "..." : "Post"}
            </button>
          </div>
          {commentMentionOpen && (
            <div style={{ marginTop: "8px", border: "1px solid #bfdbfe", borderRadius: "10px", background: "#f8fbff", overflow: "hidden" }}>
              <button
                onClick={handleCommentMentionAll}
                style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: "8px 10px", cursor: "pointer", color: "#1e3a5f" }}
              >
                @everyone
              </button>
              {commentMentionCandidates.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleCommentMentionPick(m)}
                  style={{ width: "100%", textAlign: "left", background: "none", border: "none", borderTop: "1px solid #e5eefc", padding: "8px 10px", cursor: "pointer", color: "#1e3a5f" }}
                >
                  @{m.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Composer({ currentUser, programTag, onPosted }) {
  const [type, setType] = useState("win");
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionUserIds, setMentionUserIds] = useState([]);
  const [mentionAll, setMentionAll] = useState(false);
  const fileRef = useRef();
  const tempId = "new";

  useEffect(() => {
    if (!programTag) return;
    getCommunityMembers(programTag).then(setMembers).catch((err) => {
      console.error("Failed loading community members:", err);
    });
  }, [programTag]);

  const mentionCandidates = members.filter((m) => {
    if (m.id === currentUser.uid) return false;
    const q = mentionQuery.trim().toLowerCase();
    if (!q) return true;
    return (m.name || "").toLowerCase().includes(q);
  }).slice(0, 8);

  function applyAtToken(baseText, replacement) {
    return baseText.replace(/@([\w.]*)$/, replacement);
  }

  function handleTextChange(next) {
    setText(next);
    const match = next.match(/(?:^|\s)@([\w.]*)$/);
    setMentionQuery(match ? match[1] : "");
    setMentionOpen(/(?:^|\s)@[\w.]*$/.test(next));
  }

  function handlePickMention(member) {
    setText((prev) => applyAtToken(prev, `@${member.name} `));
    setMentionUserIds((prev) => Array.from(new Set([...prev, member.id])));
    setMentionOpen(false);
  }

  function handleMentionEveryone() {
    setText((prev) => applyAtToken(prev, "@everyone "));
    setMentionAll(true);
    setMentionOpen(false);
  }

  function handleMentionTrigger() {
    setText((prev) => `${prev}${prev.endsWith(" ") || prev.length === 0 ? "" : " "}@`);
    setMentionQuery("");
    setMentionOpen(true);
  }

  async function handlePhotoAdd(files) {
    if (!files?.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const file of Array.from(files)) {
        const photo = await uploadPostPhoto(currentUser.uid, `${tempId}_${Date.now()}`, file);
        added.push(photo);
      }
      setPhotos((prev) => [...prev, ...added]);
    } catch (err) {
      console.error("Photo upload error:", err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleRemovePhoto(photoId, path) {
    await deletePostPhoto(path);
    setPhotos((prev) => prev.filter((p) => p.id !== photoId));
  }

  async function handleSubmit() {
    if (!text.trim() && photos.length === 0) return;
    setSaving(true);
    try {
      const newPostRef = await createPostWithMentions({
        programTag,
        type,
        text,
        photos,
        currentUser,
        mentionUserIds,
        mentionAll,
      });

      // Notify mentioned users
      const mentionTargets = mentionAll
        ? (members || []).filter((m) => m.id !== currentUser.uid)
        : (members || []).filter((m) => mentionUserIds.includes(m.id) && m.id !== currentUser.uid);
      mentionTargets.forEach((m) => {
        writeUserNotification({
          toUserId: m.id,
          type: "mention",
          message: `${currentUser.name || "Someone"} mentioned you in a post`,
          fromName: currentUser.name || "",
          fromAvatar: currentUser.avatar || "",
          postId: newPostRef?.id || "",
        });
      });

      // Fire admin activity event
      const typeLabel = POST_TYPES.find((t) => t.key === type)?.label || type;
      writeActivityEvent({
        type: "feed_post",
        userId: currentUser.uid,
        userName: currentUser.name || "",
        userAvatar: currentUser.avatar || "",
        message: `Posted a ${typeLabel} on the feed${text.trim() ? ` · "${text.trim().slice(0, 80)}${text.trim().length > 80 ? "…" : ""}"` : ""}`,
        meta: { postType: type, programTag },
      });

      setText("");
      setPhotos([]);
      setType("win");
      setMentionUserIds([]);
      setMentionAll(false);
      setMentionQuery("");
      setMentionOpen(false);
      setOpen(false);
      onPosted();
    } catch (err) {
      console.error("Post error:", err);
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{
        width: "100%", padding: "14px", borderRadius: "20px",
        border: "2px dashed #93c5fd", background: "#f5faff",
        color: "#6b8cb0", cursor: "pointer", fontSize: "14px",
        fontWeight: "bold", textAlign: "left",
      }}>
        ✏️ Share a win, ask a question, or check in...
      </button>
    );
  }

  return (
    <div style={card}>
      {/* Post type selector */}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "14px" }}>
        {POST_TYPES.map((t) => (
          <button key={t.key} onClick={() => setType(t.key)} style={{
            padding: "6px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: "bold",
            cursor: "pointer",
            border: type === t.key ? `2px solid ${t.border}` : "2px solid #bfdbfe",
            background: type === t.key ? t.color : "#f5faff",
            color: "#1e3a5f",
          }}>
            {t.emoji} {t.label}
          </button>
        ))}
      </div>

      {/* Text input */}
      <textarea
        value={text}
        onChange={(e) => handleTextChange(e.target.value)}
        placeholder={
          type === "win" ? "What did you crush today? 🏆" :
          type === "question" ? "What's on your mind? ❓" :
          type === "checkin" ? "How are you doing? 📸" :
          "What's up? 💬"
        }
        rows={3}
        style={{ ...inputStyle, resize: "vertical", marginBottom: "12px" }}
      />

      {mentionOpen && (
        <div style={{ marginBottom: "12px", border: "1px solid #bfdbfe", borderRadius: "10px", background: "#f8fbff", overflow: "hidden" }}>
          <button
            onClick={handleMentionEveryone}
            style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: "8px 10px", cursor: "pointer", color: "#1e3a5f" }}
          >
            @everyone
          </button>
          {mentionCandidates.map((m) => (
            <button
              key={m.id}
              onClick={() => handlePickMention(m)}
              style={{ width: "100%", textAlign: "left", background: "none", border: "none", borderTop: "1px solid #e5eefc", padding: "8px 10px", cursor: "pointer", color: "#1e3a5f" }}
            >
              @{m.name}
            </button>
          ))}
        </div>
      )}

      {/* Photo previews */}
      {photos.length > 0 && (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
          {photos.map((p) => (
            <div key={p.id} style={{ position: "relative" }}>
              <img src={p.url} alt="" style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "10px", border: "2px solid #bfdbfe" }} />
              <button onClick={() => handleRemovePhoto(p.id, p.path)} style={{
                position: "absolute", top: "3px", right: "3px", width: "18px", height: "18px",
                borderRadius: "999px", border: "none", background: "rgba(255,255,255,0.9)",
                color: "#1e3a5f", cursor: "pointer", fontSize: "10px",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={handleMentionTrigger} style={{ ...btnGhost, border: "2px solid #bfdbfe", fontWeight: "bold" }}>@ Mention</button>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ ...btnGhost, border: "2px solid #bfdbfe" }}>
          {uploading ? "Uploading..." : "📷 Add photos"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }}
          onChange={(e) => handlePhotoAdd(e.target.files)} />
        <div style={{ flex: 1 }} />
        <button onClick={() => { setOpen(false); setText(""); setPhotos([]); setMentionUserIds([]); setMentionAll(false); setMentionQuery(""); setMentionOpen(false); }} style={btnGhost}>Cancel</button>
        <button onClick={handleSubmit} disabled={saving || (!text.trim() && photos.length === 0)} style={btnPrimary}>
          {saving ? "Posting..." : "Post"}
        </button>
      </div>
    </div>
  );
}

const AVAILABLE_PROGRAMS = [
  { value: "quit-the-quit-beta", label: "Quit the Quit Beta" },
  { value: "quit-the-quit", label: "Quit the Quit" },
  { value: "momentum", label: "Momentum" },
];

export default function CommunityFeed({ currentUser, isAdmin, notifPrefs, onUpdateNotifPrefs, highlightPostId, onClearHighlight }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showNotifSettings, setShowNotifSettings] = useState(false);
  const [members, setMembers] = useState([]);

  // Admin can switch between program groups
  const [adminProgramFilter, setAdminProgramFilter] = useState(
    currentUser?.programTag || AVAILABLE_PROGRAMS[0].value
  );

  // The effective programTag to load posts for
  const effectiveProgramTag = isAdmin ? adminProgramFilter : currentUser?.programTag;

  useEffect(() => {
    if (!effectiveProgramTag) return;
    loadPosts();
  }, [effectiveProgramTag]);

  useEffect(() => {
    if (!effectiveProgramTag) return;
    getCommunityMembers(effectiveProgramTag).then(setMembers).catch((err) => {
      console.error("Failed loading community members:", err);
    });
  }, [effectiveProgramTag]);

  // When navigating to a post from a notification, reset filter and scroll to it
  useEffect(() => {
    if (!highlightPostId) return;
    setFilter("all");
  }, [highlightPostId]);

  useEffect(() => {
    if (!highlightPostId || loading) return;
    const el = document.getElementById(`post-${highlightPostId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      const timer = setTimeout(() => onClearHighlight?.(), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightPostId, loading, posts]);

  async function loadPosts() {
    setLoading(true);
    try {
      const data = await getCommunityPosts(effectiveProgramTag);
      setPosts(data);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(postId) {
    await deletePost(postId);
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  }

  async function handleAnswered(postId, answered) {
    await markQuestionAnswered(postId, answered);
    setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, isAnswered: answered } : p));
  }

  function handleEdited(postId, updates) {
    setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, ...updates, editedAt: new Date() } : p));
  }

  const filtered = filter === "all" ? posts : posts.filter((p) => p.type === filter);

  return (
    <div style={{ maxWidth: "600px", margin: "0 auto", padding: "8px 0 100px" }}>

      {/* Filter bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", flexWrap: "wrap", marginTop: "6px", marginBottom: "16px", paddingTop: "4px" }}>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", flex: "1 1 420px" }}>
          <button onClick={() => setFilter("all")} style={{
            ...btnGhost, padding: "7px 14px",
            border: filter === "all" ? "2px solid #2563eb" : "2px solid #bfdbfe",
            background: filter === "all" ? "#e0f2fe" : "none",
            fontWeight: filter === "all" ? "bold" : "normal",
            color: "#1e3a5f",
          }}>All</button>
          {POST_TYPES.map((t) => (
            <button key={t.key} onClick={() => setFilter(t.key)} style={{
              ...btnGhost, padding: "7px 14px",
              border: filter === t.key ? `2px solid ${t.border}` : "2px solid #bfdbfe",
              background: filter === t.key ? t.color : "none",
              fontWeight: filter === t.key ? "bold" : "normal",
              color: "#1e3a5f",
            }}>
              {t.emoji} {t.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <button onClick={() => setShowNotifSettings((p) => !p)} style={{ ...btnGhost, fontSize: "16px", padding: "7px 10px" }}
            title="Notification settings">🔔</button>
        </div>
      </div>

      {/* Notification preferences */}
      {showNotifSettings && (
        <div style={{ ...card, marginBottom: "14px" }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px", marginBottom: "12px" }}>
            Post notifications
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {POST_TYPES.map((t) => {
              const enabled = notifPrefs?.[t.key] !== false;
              return (
                <div key={t.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: "#1e3a5f", fontSize: "14px" }}>{t.emoji} {t.label} posts</span>
                  <button onClick={() => onUpdateNotifPrefs(t.key, !enabled)} style={{
                    width: "44px", height: "24px", borderRadius: "999px", border: "none",
                    background: enabled ? "#2563eb" : "#d1c4e9", cursor: "pointer", position: "relative",
                  }}>
                    <div style={{
                      position: "absolute", top: "2px",
                      left: enabled ? "22px" : "2px",
                      width: "20px", height: "20px", borderRadius: "999px", background: "#fff",
                      transition: "left 0.2s",
                    }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Admin program selector */}
      {isAdmin && (
        <div style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <span style={{ color: "#4b6b8a", fontWeight: "bold", fontSize: "13px", flexShrink: 0 }}>Viewing group:</span>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {AVAILABLE_PROGRAMS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setAdminProgramFilter(p.value)}
                style={{
                  padding: "6px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: "bold",
                  cursor: "pointer",
                  border: adminProgramFilter === p.value ? "2px solid #2563eb" : "2px solid #bfdbfe",
                  background: adminProgramFilter === p.value ? "#e0f2fe" : "#f5faff",
                  color: "#1e3a5f",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Composer */}
      <div style={{ marginBottom: "14px" }}>
        <Composer currentUser={currentUser} programTag={effectiveProgramTag} onPosted={loadPosts} />
      </div>

      {/* Feed */}
      {loading ? (
        <div style={{ color: "#6b8cb0", textAlign: "center", padding: "30px", fontSize: "14px" }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: "#6b8cb0", textAlign: "center", padding: "30px", fontSize: "14px" }}>
          {filter === "all" ? "No posts yet. Be the first to share! 🎉" : `No ${filter} posts yet.`}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {filtered.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              currentUser={currentUser}
              isAdmin={isAdmin}
              members={members}
              onDeleted={handleDelete}
              onAnswered={handleAnswered}
              onEdited={handleEdited}
              highlighted={post.id === highlightPostId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
